// PO email templates (/settings/po-templates) — Stephanie 2026-10-15.
// Saved emails to start a Send PO email from (the Send PO window's
// "Template" picker), laid out like the Proof email templates page. Words in
// {braces} fill in from the PO; the PO link, artwork line and sign-off are
// added automatically.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";

interface Template { id: string; name: string; subject: string; body: string }
const KEY = ["po-templates"] as const;
const EMPTY = { name: "", subject: "", body: "" };

export function PoTemplatesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ available: boolean; templates: Template[] }>("/po-templates") });
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api(editing === "new" ? "/po-templates" : `/po-templates/${editing}`, { method: editing === "new" ? "POST" : "PUT", body: JSON.stringify(form) }),
    onSuccess: () => { setEditing(null); setError(null); void qc.invalidateQueries({ queryKey: KEY }); },
    onError: (err) => {
      const b = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(b?.error ?? "We couldn't save that. Try again.");
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/po-templates/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  const open = (t: Template | null) => {
    setEditing(t ? t.id : "new");
    setForm(t ? { name: t.name, subject: t.subject, body: t.body } : EMPTY);
    setError(null);
  };

  const editor = (
    <div className="space-y-4 rounded-md border border-rule bg-white p-5 text-[15px]">
      <label className="block"><span className="mb-1 block font-semibold">Template name</span><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120} placeholder="e.g. Rush order" /></label>
      <label className="block"><span className="mb-1 block font-semibold">Subject</span><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={300} /></label>
      <label className="block"><span className="mb-1 block font-semibold">Message</span><textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={9} maxLength={10000} className="w-full rounded-md border border-rule bg-white px-3 py-2" /></label>
      <p className="text-[13px] text-muted">These fill in from the PO: {"{po_number}"}, {"{supplier_name}"}, {"{client_name}"}, {"{in_hands_date}"}, {"{ship_date}"}, {"{rep_name}"}. The PO link, the artwork line and your sign-off are added for you — just write the message.</p>
      {error && <p role="alert" className="text-sm text-risk">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!form.name.trim() || !form.subject.trim() || !form.body.trim() || save.isPending}>{save.isPending ? "Saving…" : "Save template"}</Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader eyebrow="Settings" title="PO email templates" intro="Emails to start from when sending a PO to a supplier." action={q.data?.available && editing === null ? <Button onClick={() => open(null)}>New template</Button> : undefined} />
      {q.isLoading ? null : !q.data?.available ? (
        <p className="text-muted">Templates turn on once Jake runs the database update.</p>
      ) : (
        <div className="space-y-4">
          {editing === "new" && editor}
          {q.data.templates.length === 0 && editing !== "new" && <p className="text-muted">No templates yet.</p>}
          {q.data.templates.map((t) =>
            editing === t.id ? (
              <div key={t.id}>{editor}</div>
            ) : (
              <div key={t.id} className="rounded-md border border-rule bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-ink">{t.name}</p>
                    <p className="text-sm text-muted">{t.subject}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => open(t)}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => { if (window.confirm(`Delete "${t.name}"?`)) remove.mutate(t.id); }}>Delete</Button>
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-line text-[15px] text-ink">{t.body}</p>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
